import type { ICreateOptionGroupCmd } from "@/data/commands/catalog/CreateOptionGroupCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { PlanningDao } from "@/data/dao/service/PlanningDao";
import type { IOptionGroup } from "@/data/model/OptionGroup";

import { translate } from "@/shared/localization/language";

import { MultiEntityInput } from "@/components/formControls/MultiEntityInput";

import { i18n } from "@/portal/Trans";
import { OptionAllocator } from "@/portal/elements/OptionAllocator";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";

export class OptionGroupForm extends BaseEntityForm<IOptionGroup, ICreateOptionGroupCmd> {
    private nameField: MultiLanguageInput;
    private optionAllocator: OptionAllocator;
    private displayNameToggle: Switch;
    private $internalNameField: JQuery;
    private $internalNoteField: JQuery;
    private $advancedSettingsSection: JQuery;
    private showAdvancedSettingsToggle: Switch;
    private appearsInAllOffersToggle: Switch;
    private appearsInOffersInput: MultiEntityInput;
    private $appearsInOffersContainer: JQuery;

    private appearsInAllUseCasesToggle: Switch;
    private appearsInUseCasesInput: MultiEntityInput;
    private $appearsInUseCasesContainer: JQuery;

    constructor(element: JQuery) {
        super(element, Factory.getInstance<CatalogDao>(CatalogDao).optionGroupDataSource);

        this.nameField = MultiLanguageInput.fromInput(this.$form.find('.form-control[name="name"]'));
        this.nameField.onChange = (e) => {
            this.updateName(translate(e));
        };

        this.appearsInOffersInput = MultiEntityInput.createFromInput(this.$form.find('.form-control[name="offers"]'), {
            dataSource: Factory.getInstance<CatalogDao>(CatalogDao).offerSearch,
        });

        this.appearsInUseCasesInput = MultiEntityInput.createFromInput(
            this.$form.find('.form-control[name="usecases"]'),
            {
                dataSource: Factory.getInstance<PlanningDao>(PlanningDao).useCaseSearch,
            }
        );

        this.optionAllocator = new OptionAllocator().renderTo(this.$form.find("#optionallocator"));
        this.displayNameToggle = new Switch(this.$form.find('input[name="displayname"]'));
        this.$internalNameField = this.$form.find('.form-control[name="internalname"]');
        this.$internalNoteField = this.$form.find('.form-control[name="internalnote"]');
        this.showAdvancedSettingsToggle = new Switch(this.$form.find('input[name="showadvancedsettings"]'));
        this.$advancedSettingsSection = this.$form.find("#advanced-settings-section");

        this.appearsInAllOffersToggle = new Switch(this.$form.find('input[name="appearsinalloffers"]'));
        this.$appearsInOffersContainer = this.$form.find("#appears-in-offers-container");

        this.appearsInAllUseCasesToggle = new Switch(this.$form.find('input[name="appearsinallusecases"]'));
        this.$appearsInUseCasesContainer = this.$form.find("#appears-in-use-cases-container");

        this.showAdvancedSettingsToggle.onChange = (selected) => {
            this.onChangeShowAdvancedSettings(selected);
        };

        this.appearsInAllOffersToggle.onChange = (selected) => {
            this.onChangeAppearsInAllOffers(selected);
        };

        this.appearsInAllUseCasesToggle.onChange = (selected) => {
            this.onChangeAppearsInAllUseCases(selected);
        };
    }

    private onChangeAppearsInAllOffers(selected: boolean) {
        if (selected) {
            this.$appearsInOffersContainer.hide();
        } else {
            this.$appearsInOffersContainer.show();
        }
    }

    private onChangeAppearsInAllUseCases(selected: boolean) {
        if (selected) {
            this.$appearsInUseCasesContainer.hide();
        } else {
            this.$appearsInUseCasesContainer.show();
        }
    }

    private onChangeShowAdvancedSettings(selected: boolean) {
        this.optionAllocator.setShowAdvancedSettings(selected);

        if (selected) {
            this.$advancedSettingsSection.show();
        } else {
            this.$advancedSettingsSection.hide();
        }
    }

    public setFormData(model?: IOptionGroup) {
        const enableAdvancedSettings = model == null ? false : model.enableAdvancedSettings;
        const appearsInAllOffers = model == null ? true : model.appearsInAllOffers;
        const appearsInAllUseCases = model == null ? true : model.appearsInAllUseCases;

        this.updateName(model === undefined ? "" : translate(model.name));
        this.nameField.setValues(model === undefined ? undefined : model.name);
        this.optionAllocator.setValue(model);
        this.displayNameToggle.setValue(model === undefined ? false : model.displayName);
        this.$internalNameField.val(model === undefined || model.internalName === undefined ? "" : model.internalName);
        this.$internalNoteField.val(model === undefined || model.internalNote === undefined ? "" : model.internalNote);
        this.showAdvancedSettingsToggle.setValue(enableAdvancedSettings);

        this.appearsInOffersInput.setValue(
            model === undefined
                ? undefined
                : model.appearsInOffers.map((c) => {
                      return { id: c.id, name: c.name };
                  })
        );
        this.appearsInAllOffersToggle.setValue(appearsInAllOffers);

        const sortedUseCases =
            model === undefined
                ? undefined
                : model.appearsInUseCases
                      .map((c) => {
                          return { id: c.id, name: translate(c.name) };
                      })
                      .sort((a, b) => {
                          if (b.name < a.name) return 1;
                          if (b.name > a.name) return -1;
                          return 0;
                      });

        this.appearsInUseCasesInput.setValue(sortedUseCases);
        this.appearsInAllUseCasesToggle.setValue(appearsInAllUseCases);

        this.onChangeShowAdvancedSettings(enableAdvancedSettings);
        this.onChangeAppearsInAllOffers(appearsInAllOffers);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateOptionGroupCmd {
        let data: ICreateOptionGroupCmd = {
            name: this.nameField.getValues(),
            displayName: this.displayNameToggle.getValue(),
            internalName: this.$internalNameField.val().length === 0 ? undefined : this.$internalNameField.val(),
            internalNote: this.$internalNoteField.val().length === 0 ? undefined : this.$internalNoteField.val(),
            enableAdvancedSettings: this.showAdvancedSettingsToggle.getValue(),
            appearsInAllOffers: this.appearsInAllOffersToggle.getValue(),
            appearsInOfferIds: this.appearsInOffersInput.getValue(),
            appearsInAllUseCases: this.appearsInAllUseCasesToggle.getValue(),
            appearsInUseCaseIds: this.appearsInUseCasesInput.getValue(),
            options: [],
        };

        const options = this.optionAllocator.getValue();
        data = { ...data, ...options };

        let hasError = false;

        if (this.optionAllocator.validate(data)) hasError = true;

        if (
            data.name.de === undefined &&
            data.name.en === undefined &&
            data.name.fr === undefined &&
            data.name.it === undefined
        ) {
            hasError = true;
            this.nameField.setError(true);
        } else {
            if (
                (data.name.de !== undefined && data.name.de.length > 80) ||
                (data.name.fr !== undefined && data.name.fr.length > 80) ||
                (data.name.it !== undefined && data.name.it.length > 80) ||
                (data.name.en !== undefined && data.name.en.length > 80)
            ) {
                hasError = true;
                this.nameField.setError(true);
            } else {
                this.nameField.setError(false);
            }
        }

        if (data.internalName !== undefined && data.internalName.length > 80) {
            hasError = true;
            this.$internalNameField.addClass("is-invalid");
        }

        if (data.internalNote !== undefined && data.internalNote.length > 512) {
            hasError = true;
            this.$internalNameField.addClass("is-invalid");
        }

        if (hasError) {
            this.scrollToFirstErrorField();
        }

        return (hasError ? undefined : data) as any;
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case "article_min_quantity_greater_than_option_group_max_quantity_exception":
                this.optionAllocator.validateException(b.additionalData.reason);
                this.scrollToFirstErrorField();

                return i18n("item.articleMinQuantityGreaterThanOptionGroupMaxPerOption", {
                    article: b.additionalData.article,
                    articleMinQuantity: b.additionalData.articleMinQuantity,
                    optionGroup: b.additionalData.optionGroup,
                    optionGroupMaxPerQuantity: b.additionalData.optionGroupMaxPerQuantity,
                });
        }

        return b.additionalData.reason;
    }
}
